import { useEffect, useRef, useState } from 'react';
import { Loader2, XCircle, X } from 'lucide-react';

import { usePiegeFocus } from '../hooks/usePiegeFocus';
import {
  controlerLot,
  lireReferencesRapports,
  rejeterRapport,
  type Rapport,
  type ReferencesRapport,
} from '../api/rapports';

const CLASSE_CHAMP =
  'w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all';

interface Proprietes {
  /** Les rapports à rejeter (un ou plusieurs) ; tableau vide = modale fermée. */
  readonly rapports: readonly Rapport[];
  readonly onFermer: () => void;
  /** Appelé après le rejet : l'écran parent recharge ses listes. */
  readonly onRejete?: () => void;
}

/**
 * Modale de rejet : le motif est OBLIGATOIRE, et sa longueur minimale est celle
 * que le serveur annonce — l'écran n'invente pas la règle, il l'affiche.
 */
export function ModaleRejet({ rapports, onFermer, onRejete }: Proprietes) {
  const [references, setReferences] = useState<ReferencesRapport>();
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string>();
  const premierChamp = useRef<HTMLTextAreaElement>(null);
  const boite = useRef<HTMLDivElement>(null);
  const ouvert = rapports.length > 0;
  const premier = rapports[0];
  usePiegeFocus(boite, ouvert);

  useEffect(() => {
    if (!ouvert) return;
    setMotif('');
    setErreur(undefined);
    setEnvoi(false);
    const controleur = new AbortController();
    void lireReferencesRapports({ signal: controleur.signal })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) setReferences(resultat.donnees);
      })
      .catch(() => {
        /* abandon : modale fermée */
      });
    return () => controleur.abort();
  }, [ouvert]);

  useEffect(() => {
    if (ouvert) premierChamp.current?.focus();
  }, [ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') onFermer();
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [ouvert, onFermer]);

  if (!premier) return null;

  const envoyer = async () => {
    setEnvoi(true);
    setErreur(undefined);
    // Un seul rapport : la route unitaire ; plusieurs : le lot, décidé par le
    // serveur (un compte rendu par identifiant).
    const resultat =
      rapports.length === 1
        ? await rejeterRapport(premier.id, motif)
        : await controlerLot(
            rapports.map((rapport) => rapport.id),
            'rejeter',
            motif,
          );
    setEnvoi(false);
    if (resultat.ok) {
      onRejete?.();
      onFermer();
      return;
    }
    // Le message du serveur nomme le champ et la longueur attendue.
    setErreur(resultat.message ?? 'Rejet impossible.');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      onClick={(evenement) => {
        if (evenement.target === evenement.currentTarget) onFermer();
      }}
    >
      <div
        ref={boite}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-modale-rejet"
        className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col"
      >
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
              <XCircle className="w-4 h-4" aria-hidden="true" />
            </span>
            <div>
              <h2 id="titre-modale-rejet" className="text-base font-semibold text-slate-900">
                {rapports.length === 1
                  ? 'Rejeter le compte rendu'
                  : `Rejeter ${rapports.length} comptes rendus`}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {rapports.length === 1
                  ? `${premier.client} · ${premier.dateLisible} · ${premier.typeRapport}`
                  : `${rapports.length} comptes rendus sélectionnés — un seul motif pour tous.`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onFermer}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md"
            aria-label="Fermer la fenêtre"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          {erreur && (
            <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{erreur}</p>
            </div>
          )}

          <p className="text-sm text-slate-600">
            Le prestataire verra ce motif et pourra corriger son compte rendu. Un rejet sans motif
            le laisserait devant un refus muet.
          </p>

          <div>
            <label
              htmlFor="motif-rejet"
              className="block text-sm font-medium text-slate-700 mb-1.5"
            >
              Motif du rejet <span className="text-red-600">*</span>
            </label>
            <textarea
              id="motif-rejet"
              ref={premierChamp}
              value={motif}
              onChange={(evenement) => setMotif(evenement.target.value)}
              rows={4}
              aria-describedby="aide-motif-rejet"
              className={CLASSE_CHAMP}
              placeholder="Ce qui manque ou ne va pas : essais non décrits, références absentes…"
            />
            <div className="flex justify-between items-start gap-3 mt-1">
              <p id="aide-motif-rejet" className="text-xs text-slate-500">
                {references
                  ? `Au moins ${references.motifRejetMin} caractères, ${references.motifRejetMax} au plus.`
                  : 'Le motif est obligatoire.'}
              </p>
              <p className="text-xs text-slate-500 shrink-0" aria-live="polite">
                {motif.trim().length}/{references?.motifRejetMax ?? 500}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onFermer}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void envoyer()}
            disabled={envoi}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 flex items-center gap-2"
          >
            {envoi && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            Rejeter et renvoyer au prestataire
          </button>
        </div>
      </div>
    </div>
  );
}
